import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, InputBase } from '@material-ui/core';
import { FixedSizeList } from 'react-window';
import { Droppable } from 'react-beautiful-dnd';
import debounce from 'lodash.debounce';

import { Panel, Icon, Typography, Dropdown } from '@components';
import { content } from '@content';
import { useAppDispatch } from '@store';
import { attributeLibrary, signalLibrary, userClients } from '@modules';
import { func, searchQuery, textTemplate, useLoader } from '@utils';

import { DataAttribute } from './DataAttribute';
import { DataAttributeRow } from './DataAttributeLibraryRow';

import { DataAttributeLibraryProps } from './DataAttributeLibrary.props';
import { useSignalStyles, useTreatmentStyles } from './DataAttributeLibrary.styles';
import { SEARCH_ATTRIBUTE_DELAY } from './DataAttributeLibrary.const';

/**
 DataAttributeLibrary route component.
 @returns {JSX.Element}
 */

export const DataAttributeLibrary = memo(
  ({
    businessUnit,
    treatmentPanelType,
    uiType,
    onAttributesImportSuccess = func.nop,
  }: DataAttributeLibraryProps): JSX.Element => {
    // =========================================================================
    // To be deleted
    treatmentPanelType = 'externalScoring';
    uiType = 'treatment';
    // =========================================================================

    const signalStyles = useSignalStyles();
    const treatmentStyles = useTreatmentStyles();

    const dispatch = useAppDispatch();
    const [styles, setStyles] = useState(treatmentStyles);

    const { items: attributes } = attributeLibrary.useListData();
    const filteredAttributes = useMemo(
      () =>
        treatmentPanelType === 'externalScoring'
          ? attributes.filter((attr) => ['textual', 'numeric'].includes(attr.type) && attr.signalType !== 'foreign')
          : attributes.filter((attr) => attr.signalType !== 'foreign'),
      [attributes, treatmentPanelType],
    );

    const [businessUnitId] = searchQuery.useMutualParams('businessUnit', 'program', 'deployment');

    const attributesCount = useMemo(() => filteredAttributes.length, [filteredAttributes.length]);

    const meta = attributeLibrary.useListMeta();

    useLoader(meta);

    const runSearch = debounce(
      useCallback(
        (keyword: string) => {
          dispatch(
            attributeLibrary.thunk.search({
              businessUnit,
              keyword,
            }),
          );
        },
        [dispatch, businessUnit],
      ),
      SEARCH_ATTRIBUTE_DELAY,
    );

    const handleSearchChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        runSearch(event.target.value);
      },
      [runSearch],
    );

    const fetchClient = useCallback(
      () => dispatch(userClients.thunk.getById(businessUnitId)),
      [dispatch, businessUnitId],
    );

    useEffect(() => {
      if (businessUnitId) {
        fetchClient();
      }
    }, [dispatch, fetchClient, businessUnitId]);

    useEffect(() => {
      setStyles(treatmentStyles);
    }, [treatmentStyles]);

    useEffect(() => {
      return () => {
        dispatch(signalLibrary.actions.resetListData(null));
      };
    }, []);

    // console.log('uiType', uiType);
    return (
      <Box className={styles.dataAttributeLibrary}>
        <Box className={styles.header}>
          <Typography.SuperSmallCaption className={styles.title}>
            {content.targetingAttributes} ({filteredAttributes.length})
          </Typography.SuperSmallCaption>
        </Box>

        <Box className={styles.listWrapper}>
          <Box className={styles.inputContainer}>
            <InputBase
              className={styles.searchField}
              placeholder={content.search}
              onChange={handleSearchChange}
              endAdornment={<Icon.SearchOutline />}
            />
          </Box>
          <Box className={styles.list}>
            {attributesCount === 0 && <Typography.Body align="center">{content.noItemsFound}</Typography.Body>}
            <Droppable
              droppableId="attributeLibrary"
              type="SIGNAL"
              mode="virtual"
              isDropDisabled
              renderClone={(provided, snapshot, rubric) => (
                <DataAttribute
                  className={styles.signal}
                  provided={provided}
                  item={filteredAttributes[rubric.source.index]}
                  isDragging={snapshot.isDragging}
                />
              )}
            >
              {(provided) => (
                <FixedSizeList
                  height={attributesCount ? 420 : 420}
                  width="100%"
                  itemCount={attributesCount}
                  itemSize={40}
                  outerRef={provided.innerRef}
                  itemData={filteredAttributes}
                >
                  {DataAttributeRow}
                </FixedSizeList>
              )}
            </Droppable>
          </Box>
        </Box>
      </Box>
    );
  },
);

DataAttributeLibrary.displayName = 'DataAttributeLibrary';
